import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SectionHeader } from "@/components/ui/section-header";
import { EmptyState } from "@/components/ui/empty-state";
import { IconDownload, IconHistory, IconPlayerRecord } from "@tabler/icons-react";
import { NvpStatusDot, type NvpStatus } from "@/components/nvp-status-dot";
import { CollapsibleRow } from "@/components/collapsible-row";
import { DiffBadge } from "@/components/diff-badge";
import { RecordingGrids } from "@/components/metrics-param-grid";
import {
  METRICS_KIND_ORDER,
  METRICS_KINDS,
  paramsToMap,
  type MetricsKind,
} from "@/domain/metrics-params";
import { useMetricsHistory } from "@/api/hooks";
import { timeAgo } from "@/lib/time";
import type { BuildId } from "@/types/identifiers";
import type { useMetricsActions } from "@/hooks/use-metrics-actions";
import type { Action } from "@/providers/auth-provider";
import type { components } from "@/api/generated/schema";

type HistoryEntry = components["schemas"]["MetricsHistoryEntry"];

const toParamMap = (entry: HistoryEntry) => paramsToMap(entry.parameters);

/** Seconds until the run's nominal end; holds at "finishing…" while the boat
 * evaluates the metrics and clears the trigger. */
function RecordingCountdown({ runEndsAt }: { runEndsAt: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const remaining = Math.max(0, Math.ceil((runEndsAt - now) / 1000));
  return (
    <span className="tabular-nums">{remaining > 0 ? `Recording… ${remaining}s` : "Finishing…"}</span>
  );
}

function HistoryRow({
  kind,
  entry,
  previous,
}: {
  kind: MetricsKind;
  entry: HistoryEntry;
  previous?: HistoryEntry;
}) {
  return (
    <CollapsibleRow
      border="border-l-border"
      left={
        <>
          <span className="inline-flex items-center gap-1.5 text-sm font-medium">
            <IconHistory className="h-3.5 w-3.5 text-muted-foreground" />
            {entry.event_name}
          </span>
          <span className="text-xs text-muted-foreground">{timeAgo(entry.fetched_at)}</span>
        </>
      }
      right={<DiffBadge count={entry.diff_count} />}
    >
      <RecordingGrids
        kind={kind}
        params={toParamMap(entry)}
        compare={previous ? toParamMap(previous) : undefined}
      />
    </CollapsibleRow>
  );
}

function KindBlock({
  buildId,
  kind,
  actions,
  can,
  nvpStatus,
}: {
  buildId: BuildId;
  kind: MetricsKind;
  actions: ReturnType<typeof useMetricsActions>;
  can: (action: Action) => boolean;
  nvpStatus: NvpStatus;
}) {
  const spec = METRICS_KINDS[kind];
  const { data: history, isLoading } = useMetricsHistory(buildId, kind);
  const latest = history?.[0];
  const isFetching = actions.fetchingKind === kind;
  const trigger = spec.trigger;
  const [durationS, setDurationS] = useState(trigger?.defaultDurationS ?? 20);
  const activeRecording = actions.recording?.kind === kind ? actions.recording : null;
  const isStarting = actions.startingKind === kind;
  const busy = isFetching || isStarting || !!activeRecording;

  return (
    <div className="mt-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 mb-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <SectionHeader className="mb-0">{spec.label}</SectionHeader>
          {latest && (
            <span className="text-xs text-muted-foreground">
              {latest.event_name} · {timeAgo(latest.fetched_at)}
            </span>
          )}
        </div>
        {can("manage-calibrations") && (
          <div className="flex flex-wrap items-center gap-3">
            <NvpStatusDot
              status={nvpStatus}
              label={spec.nvp === "wing" ? "Wing NVP" : "Platform NVP"}
            />
            {trigger && (
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  inputMode="numeric"
                  min={trigger.minS}
                  max={trigger.maxS}
                  value={durationS}
                  onChange={(e) => setDurationS(Number(e.target.value))}
                  disabled={busy}
                  aria-label="Recording duration (seconds)"
                  className="w-14 px-1.5 py-1 text-sm text-right tabular-nums border border-input bg-card rounded focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <span className="text-xs text-muted-foreground">s</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => actions.handleStart(kind, durationS)}
                  disabled={
                    busy ||
                    nvpStatus !== "online" ||
                    !(durationS >= trigger.minS && durationS <= trigger.maxS)
                  }
                >
                  <IconPlayerRecord className="h-4 w-4 mr-1.5 text-red-500" />
                  {activeRecording ? (
                    <RecordingCountdown runEndsAt={activeRecording.runEndsAt} />
                  ) : isStarting ? (
                    "Starting…"
                  ) : (
                    "Start recording"
                  )}
                </Button>
              </div>
            )}
            <Button
              size="sm"
              onClick={() => actions.handleFetch(kind)}
              disabled={busy || nvpStatus !== "online"}
            >
              <IconDownload className="h-4 w-4 mr-1.5" />
              {isFetching ? "Recording..." : "Record from NVP"}
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <Skeleton className="h-24 w-full rounded-r-lg" />
      ) : !latest ? (
        <EmptyState message={`No ${spec.label} metrics have been recorded for this boat yet.`} />
      ) : (
        <div className="space-y-2">
          <div className="bg-card border-l-3 border-l-emerald-500 rounded-r-lg pl-4 pr-5 py-3 overflow-x-auto">
            <div className="flex items-baseline gap-2 text-xs text-muted-foreground mb-2">
              <span>
                Latest recording — {latest.event_name}, {timeAgo(latest.fetched_at)} by{" "}
                {latest.fetched_by}
              </span>
              <DiffBadge count={latest.diff_count} />
            </div>
            <RecordingGrids
              kind={kind}
              params={toParamMap(latest)}
              compare={history?.[1] ? toParamMap(history[1]) : undefined}
            />
          </div>
          {history
            ?.slice(1)
            .map((entry, i) => (
              <HistoryRow key={entry.id} kind={kind} entry={entry} previous={history[i + 2]} />
            ))}
        </div>
      )}
    </div>
  );
}

export function MetricsSection({
  buildId,
  actions,
  can,
  platformNvpStatus,
  wingNvpStatus,
}: {
  buildId: BuildId;
  actions: ReturnType<typeof useMetricsActions>;
  can: (action: Action) => boolean;
  platformNvpStatus: NvpStatus;
  wingNvpStatus: NvpStatus;
}) {
  return (
    <div>
      {METRICS_KIND_ORDER.map((kind) => (
        <KindBlock
          key={kind}
          buildId={buildId}
          kind={kind}
          actions={actions}
          can={can}
          nvpStatus={METRICS_KINDS[kind].nvp === "wing" ? wingNvpStatus : platformNvpStatus}
        />
      ))}
    </div>
  );
}
